import { buildVariantSpecificationsPayload, validateVariantSpecifications } from './variantSpecifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Check, ChevronRight, Circle, Pencil, Plus, Trash2 } from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  CurrencyInput,
  CatalogProductMediaManager,
  CatalogProductWorkspaceModeSwitch,
  DescriptionText,
  Field,
  HorizontalStack,
  PaneHeader,
  PaneSection,
  PercentageInput,
  ProductEditorSheet,
  RegistryStatus,
  SilkInput,
  SilkSelect,
  Surface,
  VariantOfferEditorSheet,
  VariantEditorSheet,
  VariantSpecificationsDisplay,
  FeatureIcon,
  FeatureIconPicker,
  VerticalStack,
  toast,
  type ProductEditorFormValue,
  type ProductEditorViewModel,
  type CatalogProductWorkspaceModeSwitchViewModel,
  type VariantOfferEditorViewModel,
  type VariantEditorFormValue,
  type VariantEditorViewModel,
} from '@workspace/silk-ui';

import {
  archiveProduct,
  archiveVariant,
  archiveVariantBundleComponent,
  archiveVariantFeature,
  archiveVariantOffer,
  archiveVariantRecommendation,
  createVariantBundleComponent,
  createVariantFeature,
  createVariant,
  createVariantOffer,
  createVariantRecommendation,
  getCatalog,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getProduct,
  listCatalogProducts,
  listAttributeDefinitions,
  listPricebooks,
  updateProduct,
  updateVariantBundleComponent,
  updateVariantFeature,
  updateVariantOffer,
  updateVariantRecommendation,
  updateVariant,
  type Catalog,
  type CommerceAttributeDefinition,
  type Pricebook,
  type Product,
  type VariantBundleComponent,
  type VariantFeature,
  type ProductVariant,
  type ProductVariantOffer,
  type VariantRecommendation,
} from '@/lib/commerce';
import {
  buildCatalogDirectoryRoute,
  buildCatalogProductRoute,
  buildCatalogRoute,
  buildPricebooksListRoute,
} from '@/lib/commerce-routing';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery, withCommerceQueryValue, withCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { useCatalogProductMediaController } from '@/features/commerce-admin/useCatalogProductMediaController';
import {
  formatMinorCurrency,
  formatMinorCurrencyInput,
  parseCurrencyInputToMinor,
} from '@/features/commerce-admin/currency';
import { CommerceRegistrySearchField } from './CommerceRegistrySearchField';
import {
  buildProductSelectorOptions,
  buildVariantSelectorOptions,
} from './selectorOptions';
import {
  attributeDefinitionsToViewModels,
  buildVariantAttributePayload,
  initialVariantAttributeValue,
  mapVariantAttributeServiceError,
} from './variantAttributes';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
  emptyOfferForm,
  equipmentLeaseRecurringOfferType,
  emptyProductForm,
  emptyVariantForm,
  flattenOptionValueOptions,
  formatRelativeTime,
  formatAllowedTermMonthsText,
  metadataEntriesToRecord,
  offerToForm,
  productBrowseVisibilityOptions,
  productToForm,
  type ValidationMap,
  validateOfferForm,
  validateProductForm,
  validateVariantForm,
  variantToForm,
  type EditorMode,
} from './catalogHierarchyShared';

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'published':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

function sellableVariant(sellable: boolean): 'success' | 'muted' {
  return sellable ? 'success' : 'muted';
}

function buildOfferCommercialSummary(offer: ProductVariantOffer): string | null {
  if (offer.offer_type !== equipmentLeaseRecurringOfferType) {
    return null;
  }
  const leaseTerms = offer.lease_terms || {
    allowed_term_months: offer.allowed_term_months,
    minimum_upfront_payment_amount_minor: offer.minimum_upfront_payment_amount_minor,
    maximum_upfront_payment_amount_minor: offer.maximum_upfront_payment_amount_minor,
    purchase_cost_currency: offer.purchase_cost_currency,
    intended_sale_price_currency: undefined,
  };
  const allowedTermsLabel = formatAllowedTermMonthsText(leaseTerms.allowed_term_months);
  const minimumUpfront = leaseTerms.minimum_upfront_payment_amount_minor;
  const maximumUpfront = leaseTerms.maximum_upfront_payment_amount_minor;
  const currency = leaseTerms.intended_sale_price_currency || leaseTerms.purchase_cost_currency || 'USD';
  const upfrontLabel =
    minimumUpfront === undefined ||
    minimumUpfront === null ||
    maximumUpfront === undefined ||
    maximumUpfront === null
      ? null
      : `${formatMinorCurrency(minimumUpfront, currency)} to ${formatMinorCurrency(maximumUpfront, currency)} upfront`;
  return [allowedTermsLabel ? `Terms ${allowedTermsLabel}` : null, upfrontLabel].filter(Boolean).join(' • ') || null;
}

function offerAllowedTermMonths(offer: ProductVariantOffer): number[] | undefined {
  return offer.lease_terms?.allowed_term_months || offer.allowed_term_months;
}

function SetupStep({
  done,
  warn,
  label,
  onClick,
}: {
  done: boolean;
  warn?: boolean;
  label: string;
  onClick?: () => void;
}) {
  const icon = done ? (
    <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
  ) : warn ? (
    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
  ) : (
    <Circle className="h-3 w-3 text-muted-foreground/40" />
  );
  const textClass = warn
    ? 'text-amber-600 dark:text-amber-400'
    : done
      ? 'text-muted-foreground'
      : 'text-foreground';

  if (!onClick) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs font-semibold ${textClass}`}>
        {icon}
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold transition-colors hover:bg-muted/40 ${textClass}`}
    >
      {icon}
      {label}
    </button>
  );
}

type RelatedItemKind = 'suggested' | 'included';

type RelatedItemFormState = {
  kind: RelatedItemKind;
  productId: string;
  variantId: string;
  required: boolean;
  defaultQuantity: string;
  bundlePriceMode: 'standard' | 'included_free' | 'fixed_price' | 'amount_off' | 'percent_off';
  bundlePriceAmount: string;
  bundlePricePercentage: string;
  reason: string;
  sortOrder: string;
};

function emptyRelatedItemForm(): RelatedItemFormState {
  return {
    kind: 'suggested',
    productId: '',
    variantId: '',
    required: true,
    defaultQuantity: '1',
    bundlePriceMode: 'standard',
    bundlePriceAmount: '',
    bundlePricePercentage: '',
    reason: '',
    sortOrder: '0',
  };
}

function recommendationToRelatedItemForm(recommendation: VariantRecommendation): RelatedItemFormState {
  return {
    ...emptyRelatedItemForm(),
    kind: 'suggested',
    productId: recommendation.recommended_product_id || '',
    variantId: recommendation.recommended_variant_id || '',
    reason: recommendation.reason || '',
    sortOrder: String(recommendation.sort_order ?? 0),
  };
}

function bundleComponentToRelatedItemForm(
  component: VariantBundleComponent,
  currency: string,
): RelatedItemFormState {
  return {
    ...emptyRelatedItemForm(),
    kind: 'included',
    productId: component.component_product_id || '',
    variantId: component.component_variant_id || '',
    required: component.required,
    defaultQuantity: String(component.default_quantity || 1),
    bundlePriceMode: component.bundle_price_mode || 'standard',
    bundlePriceAmount: formatMinorCurrencyInput(component.bundle_price_amount_minor, currency),
    bundlePricePercentage:
      component.bundle_price_percentage == null ? '' : String(component.bundle_price_percentage),
  };
}

function validateRelatedItemForm(value: RelatedItemFormState, currency: string): ValidationMap {
  const nextValidation: ValidationMap = {};
  if (!value.productId && !value.variantId) {
    nextValidation.productId = 'Select a product or variant.';
  }
  if (value.kind === 'included') {
    if (!/^\d+$/.test(value.defaultQuantity.trim()) || Number.parseInt(value.defaultQuantity.trim(), 10) <= 0) {
      nextValidation.defaultQuantity = 'Default quantity must be a positive whole number.';
    }
    if (value.bundlePriceMode === 'fixed_price' || value.bundlePriceMode === 'amount_off') {
      try {
        const amountMinor = parseCurrencyInputToMinor(value.bundlePriceAmount, currency, 'Bundle price');
        if (amountMinor == null) {
          nextValidation.bundlePriceAmount = 'Enter a valid currency amount.';
        } else if (value.bundlePriceMode === 'amount_off' && amountMinor <= 0) {
          nextValidation.bundlePriceAmount = 'Amount off must be greater than zero.';
        }
      } catch (error) {
        nextValidation.bundlePriceAmount = error instanceof Error ? error.message : 'Enter a valid currency amount.';
      }
    }
    if (value.bundlePriceMode === 'percent_off') {
      const percentage = Number(value.bundlePricePercentage);
      if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
        nextValidation.bundlePricePercentage = 'Percentage must be greater than 0 and no more than 100.';
      }
    }
  } else if (!/^-?\d+$/.test(value.sortOrder.trim())) {
    nextValidation.sortOrder = 'Sort order must be a whole number.';
  }
  return nextValidation;
}

type VariantFeatureFormState = {
  description: string;
  icon: string;
  sortOrder: string;
};

function emptyFeatureForm(): VariantFeatureFormState {
  return {
    description: '',
    icon: '',
    sortOrder: '0',
  };
}

function featureToForm(feature: VariantFeature): VariantFeatureFormState {
  return {
    description: feature.description || '',
    icon: feature.icon || '',
    sortOrder: String(feature.sort_order ?? 0),
  };
}

function validateFeatureForm(value: VariantFeatureFormState): ValidationMap {
  const nextValidation: ValidationMap = {};
  if (!value.description.trim()) {
    nextValidation.description = 'Description is required.';
  }
  if (!/^-?\d+$/.test(value.sortOrder.trim())) {
    nextValidation.sortOrder = 'Sort order must be a whole number.';
  }
  return nextValidation;
}

function RelatedItemEditorSheet({
  mode,
  isOpen,
  saving,
  variantLabel,
  form,
  validationErrors,
  productOptions,
  variantOptions,
  onClose,
  onKindChange,
  onProductChange,
  onVariantChange,
  onChange,
  onSave,
}: {
  mode: EditorMode;
  isOpen: boolean;
  saving: boolean;
  variantLabel: string;
  form: RelatedItemFormState;
  validationErrors: ValidationMap;
  productOptions: Array<{ value: string; label: string; description?: string | null }>;
  variantOptions: Array<{ value: string; label: string; description?: string | null }>;
  onClose: () => void;
  onKindChange: (kind: RelatedItemKind) => void;
  onProductChange: (value: string) => void;
  onVariantChange: (value: string) => void;
  onChange: (patch: Partial<RelatedItemFormState>) => void;
  onSave: () => Promise<void>;
}) {
  if (!isOpen) {
    return null;
  }

  const kindOptions: Array<{ value: RelatedItemKind; label: string; description: string }> = [
    {
      value: 'suggested',
      label: 'Suggested',
      description: 'Shown as an optional suggestion during guided selling. Never added to the cart automatically.',
    },
    {
      value: 'included',
      label: 'Included in bundle',
      description: 'Automatically included with this variant at checkout.',
    },
  ];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'edit' ? 'Edit related item' : 'Add related item'}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl animate-in slide-in-from-right duration-300 sm:max-w-3xl"
      >
        <PaneHeader
          title={mode === 'edit' ? 'Edit Related Item' : 'Add Related Item'}
          subtitle={`Connect another catalog item to ${variantLabel}, either as an included bundle item or an optional suggestion.`}
          onClear={onClose}
        />

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <VerticalStack gap={6}>
            <PaneSection label="Relationship">
              <div role="radiogroup" aria-label="Relationship type" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {kindOptions.map(option => {
                  const selected = form.kind === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={mode === 'edit'}
                      onClick={() => onKindChange(option.value)}
                      className={
                        'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
                        (selected
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-border/60 bg-muted/10 hover:border-primary/30')
                      }
                    >
                      <p className="text-sm font-semibold text-foreground">{option.label}</p>
                      <DescriptionText className="mt-1 text-[11px]">{option.description}</DescriptionText>
                    </button>
                  );
                })}
              </div>
              {mode === 'edit' ? (
                <DescriptionText className="text-[11px]">
                  The relationship type cannot be changed after creation. Delete this item and add a new one to switch.
                </DescriptionText>
              ) : null}
            </PaneSection>

            <PaneSection label="Target">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Product"
                  error={validationErrors.productId}
                  description="Choose a product when any of its variants is a suitable match."
                >
                  <SilkSelect value={form.productId} onValueChange={onProductChange}>
                    <option value="">None</option>
                    {productOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SilkSelect>
                </Field>

                <Field
                  label="Variant"
                  description="Choose a concrete variant when it must resolve to one exact item."
                >
                  <SilkSelect value={form.variantId} onValueChange={onVariantChange}>
                    <option value="">None</option>
                    {variantOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SilkSelect>
                </Field>
              </div>
            </PaneSection>

            {form.kind === 'included' ? (
              <PaneSection label="Bundle Rules">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Default Quantity"
                    error={validationErrors.defaultQuantity}
                    description="How many units are included when the parent variant is added."
                  >
                    <SilkInput
                      type="number"
                      min="1"
                      value={form.defaultQuantity}
                      onChange={event => onChange({ defaultQuantity: event.target.value })}
                    />
                  </Field>

                  <Field
                    label="Required"
                    description="Required items cannot be removed while the parent variant remains selected."
                  >
                    <label className="inline-flex min-h-10 items-center gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-bold text-foreground">
                      <input
                        type="checkbox"
                        checked={form.required}
                        onChange={event => onChange({ required: event.target.checked })}
                      />
                      <span>Cannot be removed at checkout</span>
                    </label>
                  </Field>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Included Item Price"
                    description="Controls only the bundled item's product or offer charge. Its configured fees still apply."
                  >
                    <SilkSelect
                      value={form.bundlePriceMode}
                      onValueChange={value =>
                        onChange({
                          bundlePriceMode: value as RelatedItemFormState['bundlePriceMode'],
                          bundlePriceAmount: '',
                          bundlePricePercentage: '',
                        })
                      }
                    >
                      <option value="standard">Standard price</option>
                      <option value="included_free">Included free ($0.00)</option>
                      <option value="fixed_price">Fixed bundle price</option>
                      <option value="amount_off">Amount off</option>
                      <option value="percent_off">Percentage off</option>
                    </SilkSelect>
                  </Field>

                  {form.bundlePriceMode === 'fixed_price' || form.bundlePriceMode === 'amount_off' ? (
                    <Field
                      label={form.bundlePriceMode === 'fixed_price' ? 'Fixed Price' : 'Amount Off'}
                      error={validationErrors.bundlePriceAmount}
                      description="Enter the customer-facing currency amount."
                    >
                      <CurrencyInput
                        aria-label={form.bundlePriceMode === 'fixed_price' ? 'Fixed Price' : 'Amount Off'}
                        value={form.bundlePriceAmount}
                        onChange={event => onChange({ bundlePriceAmount: event.target.value })}
                      />
                    </Field>
                  ) : form.bundlePriceMode === 'percent_off' ? (
                    <Field
                      label="Percentage Off"
                      error={validationErrors.bundlePricePercentage}
                      description="Enter a percentage greater than 0 and no more than 100."
                    >
                      <PercentageInput
                        aria-label="Percentage Off"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={form.bundlePricePercentage}
                        onChange={event => onChange({ bundlePricePercentage: event.target.value })}
                      />
                    </Field>
                  ) : (
                    <DescriptionText className="self-end pb-2 text-[11px]">
                      {form.bundlePriceMode === 'included_free'
                        ? 'The included item charge becomes $0.00; independent fees remain billable.'
                        : 'The included item keeps its normal configured price.'}
                    </DescriptionText>
                  )}
                </div>
              </PaneSection>
            ) : (
              <PaneSection label="Guidance">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_160px]">
                  <Field
                    label="Reason"
                    description="Explain why this item is suggested, for example 'Best fit for gigabit plans'."
                  >
                    <textarea
                      value={form.reason}
                      onChange={event => onChange({ reason: event.target.value })}
                      rows={4}
                      className="w-full rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-bold text-foreground outline-none transition-all focus:border-primary/40 focus:bg-background"
                    />
                  </Field>
                  <Field
                    label="Sort Order"
                    error={validationErrors.sortOrder}
                    description="Lower values appear first."
                  >
                    <SilkInput
                      type="number"
                      value={form.sortOrder}
                      onChange={event => onChange({ sortOrder: event.target.value })}
                    />
                  </Field>
                </div>
              </PaneSection>
            )}
          </VerticalStack>
        </div>

        <div className="border-t border-border/60 px-6 py-4">
          <HorizontalStack className="justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-bold text-foreground transition-all hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
            >
              {mode === 'edit' ? 'Save Related Item' : 'Add Related Item'}
            </button>
          </HorizontalStack>
        </div>
      </div>
    </>
  );
}

function FeatureEditorSheet({
  mode,
  isOpen,
  saving,
  variantLabel,
  form,
  validationErrors,
  onClose,
  onChange,
  onSave,
}: {
  mode: EditorMode;
  isOpen: boolean;
  saving: boolean;
  variantLabel: string;
  form: VariantFeatureFormState;
  validationErrors: ValidationMap;
  onClose: () => void;
  onChange: (patch: Partial<VariantFeatureFormState>) => void;
  onSave: () => Promise<void>;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'edit' ? 'Edit feature' : 'Create feature'}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl animate-in slide-in-from-right duration-300 sm:max-w-3xl"
      >
        <PaneHeader
          title={mode === 'edit' ? 'Edit Feature' : 'Create Feature'}
          subtitle={`Manage storefront comparison bullets for ${variantLabel}.`}
          onClear={onClose}
        />

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <VerticalStack gap={6}>
            <Surface className="border-primary/20 bg-primary/5 p-4">
              <DescriptionText className="text-xs">
                Features are short selling points and customer benefits. Put detailed technical information in Specifications.
              </DescriptionText>
            </Surface>

            <PaneSection label="Feature Details">
              <VerticalStack gap={4}>
                <Field
                  label="Description"
                  error={validationErrors.description}
                  description="Short buyer-facing feature summary, such as Wi-Fi 7 included or Symmetrical upload speeds."
                >
                  <SilkInput
                    value={form.description}
                    onChange={(event: { target: { value: string } }) => onChange({ description: event.target.value })}
                  />
                </Field>

                <Field label="Icon" description="Choose a suggested key or enter a custom icon name.">
                  <FeatureIconPicker value={form.icon} onChange={icon => onChange({ icon })} />
                </Field>

                <Field
                  label="Sort Order"
                  error={validationErrors.sortOrder}
                  description="Lower numbers appear first."
                >
                  <SilkInput
                    value={form.sortOrder}
                    onChange={(event: { target: { value: string } }) => onChange({ sortOrder: event.target.value })}
                  />
                </Field>
              </VerticalStack>
            </PaneSection>
          </VerticalStack>
        </div>

        <div className="border-t border-border bg-background px-6 py-4">
          <HorizontalStack className="justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-md border border-border/60 bg-background px-4 py-2 text-xs font-black uppercase tracking-tight text-muted-foreground hover:border-primary/40 hover:text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-xs font-black uppercase tracking-tight text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : mode === 'edit' ? 'Save Feature' : 'Create Feature'}
            </button>
          </HorizontalStack>
        </div>
      </div>
    </>
  );
}

export default function ProductVariantsPage({
  catalogId,
  productId,
}: {
  catalogId: string;
  productId: string;
}) {
  const { activeTenant } = useScope();
  const navigation = useNavigation();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [pricebooks, setPricebooks] = useState<Pricebook[] | null>(null);
  const [attributeDefinitions, setAttributeDefinitions] = useState<CommerceAttributeDefinition[]>([]);
  const [selectorProductDetailsById, setSelectorProductDetailsById] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({ facetKeys: [] });
  const searchQuery = query.search;

  const [productEditorOpen, setProductEditorOpen] = useState(false);
  const [productForm, setProductForm] = useState<ProductEditorFormValue>(emptyProductForm);
  const [productValidation, setProductValidation] = useState<ValidationMap>({});

  const [variantEditorMode, setVariantEditorMode] = useState<EditorMode>('create');
  const [variantEditorOpen, setVariantEditorOpen] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [variantForm, setVariantForm] = useState<VariantEditorFormValue>(emptyVariantForm);
  const [variantValidation, setVariantValidation] = useState<ValidationMap>({});
  const [variantAttributesDirty, setVariantAttributesDirty] = useState(false);
  const [variantSpecificationsDirty, setVariantSpecificationsDirty] = useState(false);
  const attributeRowSequence = useRef(0);
  const [offerEditorMode, setOfferEditorMode] = useState<EditorMode>('create');
  const [offerEditorOpen, setOfferEditorOpen] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [offerValidation, setOfferValidation] = useState<ValidationMap>({});
  const [relatedItemEditorMode, setRelatedItemEditorMode] = useState<EditorMode>('create');
  const [relatedItemEditorOpen, setRelatedItemEditorOpen] = useState(false);
  const [editingRelatedItemId, setEditingRelatedItemId] = useState<string | null>(null);
  const [relatedItemForm, setRelatedItemForm] = useState<RelatedItemFormState>(emptyRelatedItemForm);
  const [relatedItemValidation, setRelatedItemValidation] = useState<ValidationMap>({});
  const [featureEditorMode, setFeatureEditorMode] = useState<EditorMode>('create');
  const [featureEditorOpen, setFeatureEditorOpen] = useState(false);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [featureForm, setFeatureForm] = useState<VariantFeatureFormState>(emptyFeatureForm);
  const [featureValidation, setFeatureValidation] = useState<ValidationMap>({});
  const [savingKind, setSavingKind] = useState<'product' | 'variant' | 'offer' | 'feature' | 'related-item' | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<'offers' | 'specifications' | 'merchandising'>('offers');
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const attributeDefinitionViewModels = useMemo(
    () => attributeDefinitionsToViewModels(attributeDefinitions),
    [attributeDefinitions],
  );
  const filteredVariants = useMemo(() => {
    const variants = product?.variants || [];
    if (!normalizedSearch) {
      return variants;
    }

    return variants.filter(variant =>
      [
        variant.name,
        variant.variant_key,
        variant.display_name,
        variant.fulfillment_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [product, normalizedSearch]);

  // The selected variant lives in the URL (?variant=...) so the view is shareable
  // and survives reloads; an invalid or missing id falls back to the first variant.
  const selectedVariantId = useMemo(() => {
    const queryIndex = navigation.path.indexOf('?');
    if (queryIndex === -1) {
      return null;
    }
    return new URLSearchParams(navigation.path.slice(queryIndex + 1)).get('variant');
  }, [navigation.path]);

  const workspaceMode = useMemo(() => {
    const queryIndex = navigation.path.indexOf('?');
    if (queryIndex === -1) {
      return 'variants' as const;
    }
    return new URLSearchParams(navigation.path.slice(queryIndex + 1)).get('view') === 'images'
      ? 'images' as const
      : 'variants' as const;
  }, [navigation.path]);

  const selectWorkspaceMode = (mode: 'variants' | 'images') => {
    navigation.navigate(
      withCommerceQueryValue(navigation.path, 'view', mode === 'images' ? 'images' : null),
      { replace: true },
    );
    if (mode === 'variants') {
      setMobileShowDetail(false);
    }
  };

  const selectVariant = (variantId: string) => {
    navigation.navigate(withCommerceRegistryQuery(
      `${buildCatalogProductRoute(catalogId, productId)}?variant=${encodeURIComponent(variantId)}`,
      navigation.path,
    ), { replace: true });
  };

  const selectedVariant = useMemo(() => {
    const variants = product?.variants || [];
    if (variants.length === 0) {
      return null;
    }
    return variants.find(variant => variant.variant_id === selectedVariantId) || variants[0];
  }, [product, selectedVariantId]);

  const hydratedCatalogProducts = useMemo(
    () => catalogProducts.map(item => selectorProductDetailsById[item.product_id] || item),
    [catalogProducts, selectorProductDetailsById],
  );

  const loadPage = async () => {
    if (!activeTenant?.id) {
      setCatalog(null);
      setProduct(null);
      setCatalogProducts([]);
      setAttributeDefinitions([]);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);

    try {
      const [nextCatalog, nextProduct, catalogProductPage, nextPricebooks, nextAttributeDefinitions] = await Promise.all([
        getCatalog(catalogId),
        getProduct(productId),
        listCatalogProducts(catalogId),
        // Pricing coverage is a nice-to-have signal; never block the page on it.
        listPricebooks({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items).catch(() => null),
        listAttributeDefinitions(activeTenant.id),
      ]);
      const nextCatalogProducts = catalogProductPage.items;

      if (nextProduct.catalog_id !== catalogId) {
        throw new Error('Product does not belong to the requested catalog.');
      }

      setCatalog(nextCatalog);
      setProduct(nextProduct);
      setCatalogProducts(nextCatalogProducts);
      setPricebooks(nextPricebooks);
      setAttributeDefinitions(nextAttributeDefinitions);
      setSelectorProductDetailsById(current => ({
        ...current,
        [nextProduct.product_id]: nextProduct,
        ...Object.fromEntries(
          nextCatalogProducts
            .filter(item => (item.variants?.length || 0) > 0)
            .map(item => [item.product_id, item]),
        ),
      }));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load product variants.'));
      }
      setCatalog(null);
      setProduct(null);
      setCatalogProducts([]);
      setAttributeDefinitions([]);
      setSelectorProductDetailsById({});
    } finally {
      setLoading(false);
    }
  };

  // Targeted refetch after mutations: only the product changes, so the catalog
  // and sibling-product caches are left alone instead of reloading the page.
  const refreshProduct = async () => {
    try {
      const nextProduct = await getProduct(productId);
      if (nextProduct.catalog_id !== catalogId) {
        throw new Error('Product does not belong to the requested catalog.');
      }
      setProduct(nextProduct);
      setCatalogProducts(current =>
        current.map(item => (item.product_id === nextProduct.product_id ? nextProduct : item)),
      );
      setSelectorProductDetailsById(current => ({
        ...current,
        [nextProduct.product_id]: nextProduct,
      }));
      return nextProduct;
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to refresh the product.'));
      }
      return null;
    }
  };

  useEffect(() => {
    void loadPage();
  }, [activeTenant?.id, catalogId, productId]);

  useEffect(() => {
    if (!relatedItemEditorOpen) {
      return;
    }

    const missingProductIds = catalogProducts
      .map(item => item.product_id)
      .filter(productId => (selectorProductDetailsById[productId]?.variants?.length || 0) === 0);
    if (missingProductIds.length === 0) {
      return;
    }

    let cancelled = false;
    void Promise.all(
      missingProductIds.map(async productId => {
        try {
          return await getProduct(productId);
        } catch {
          return null;
        }
      }),
    ).then(results => {
      if (cancelled) {
        return;
      }
      setSelectorProductDetailsById(current => {
        const next = { ...current };
        let changed = false;
        results.forEach(item => {
          if (!item) {
            return;
          }
          next[item.product_id] = item;
          changed = true;
        });
        return changed ? next : current;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [catalogProducts, relatedItemEditorOpen, selectorProductDetailsById]);

  const productLabelById = useMemo(() => {
    const next = new Map<string, string>();
    hydratedCatalogProducts.forEach(item => {
      next.set(item.product_id, item.name);
    });
    return next;
  }, [hydratedCatalogProducts]);

  const variantDetailsById = useMemo(() => {
    const next = new Map<string, { label: string; productId: string }>();
    hydratedCatalogProducts.forEach(item => {
      item.variants.forEach(variant => {
        next.set(variant.variant_id, {
          label: `${item.name} / ${variant.display_name || variant.name}`,
          productId: item.product_id,
        });
      });
    });
    return next;
  }, [hydratedCatalogProducts]);

  const relatedItemProductOptions = useMemo(() => {
    const options = buildProductSelectorOptions(hydratedCatalogProducts);
    // A variant cannot bundle its own product, but it may suggest a sibling variant.
    return relatedItemForm.kind === 'included'
      ? options.filter(option => option.value !== product?.product_id)
      : options;
  }, [hydratedCatalogProducts, product?.product_id, relatedItemForm.kind]);

  const relatedItemVariantOptions = useMemo(() => {
    const scopeProducts = relatedItemForm.productId
      ? hydratedCatalogProducts.filter(item => item.product_id === relatedItemForm.productId)
      : hydratedCatalogProducts;

    return buildVariantSelectorOptions(scopeProducts).filter(option => option.value !== selectedVariant?.variant_id);
  }, [hydratedCatalogProducts, relatedItemForm.productId, selectedVariant?.variant_id]);

  const openProductEdit = () => {
    if (!product) {
      return;
    }
    setProductForm(productToForm(product));
    setProductValidation({});
    setProductEditorOpen(true);
  };

  const openVariantCreate = () => {
    setVariantEditorMode('create');
    setEditingVariantId(null);
    setVariantForm(emptyVariantForm());
    setVariantValidation({});
    setVariantAttributesDirty(false);
    setVariantSpecificationsDirty(false);
    setVariantEditorOpen(true);
  };

  const openVariantEdit = (variant: ProductVariant) => {
    setVariantEditorMode('edit');
    setEditingVariantId(variant.variant_id);
    setVariantForm(variantToForm(variant));
    setVariantValidation({});
    setVariantAttributesDirty(false);
    setVariantSpecificationsDirty(false);
    setVariantEditorOpen(true);
  };

  const openOfferCreate = () => {
    if (!selectedVariant) {
      return;
    }
    setOfferEditorMode('create');
    setEditingOfferId(null);
    setOfferForm(emptyOfferForm());
    setOfferValidation({});
    setOfferEditorOpen(true);
  };

  const openOfferEdit = (offer: ProductVariantOffer) => {
    setOfferEditorMode('edit');
    setEditingOfferId(offer.offer_id);
    setOfferForm(offerToForm(offer));
    setOfferValidation({});
    setOfferEditorOpen(true);
  };

  const openRelatedItemCreate = () => {
    if (!selectedVariant) {
      return;
    }
    setRelatedItemEditorMode('create');
    setEditingRelatedItemId(null);
    setRelatedItemForm(emptyRelatedItemForm());
    setRelatedItemValidation({});
    setRelatedItemEditorOpen(true);
  };

  const openFeatureCreate = () => {
    setFeatureEditorMode('create');
    setEditingFeatureId(null);
    setFeatureForm(emptyFeatureForm());
    setFeatureValidation({});
    setFeatureEditorOpen(true);
  };

  const openFeatureEdit = (feature: VariantFeature) => {
    setFeatureEditorMode('edit');
    setEditingFeatureId(feature.feature_id);
    setFeatureForm(featureToForm(feature));
    setFeatureValidation({});
    setFeatureEditorOpen(true);
  };

  const openRecommendationEdit = (recommendation: VariantRecommendation) => {
    setRelatedItemEditorMode('edit');
    setEditingRelatedItemId(recommendation.recommendation_id);
    setRelatedItemForm(recommendationToRelatedItemForm(recommendation));
    setRelatedItemValidation({});
    setRelatedItemEditorOpen(true);
  };

  const openBundleComponentEdit = (component: VariantBundleComponent) => {
    setRelatedItemEditorMode('edit');
    setEditingRelatedItemId(component.bundle_component_id);
    setRelatedItemForm(bundleComponentToRelatedItemForm(component, catalog?.default_currency || 'USD'));
    setRelatedItemValidation({});
    setRelatedItemEditorOpen(true);
  };

  const saveProduct = async () => {
    if (!product) {
      return;
    }

    const nextValidation = validateProductForm(productForm);
    setProductValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('product');
    setError(null);

    try {
      await updateProduct(product.product_id, {
        name: productForm.identity.name.trim(),
        short_description: productForm.identity.shortDescription || '',
        description: productForm.identity.description || '',
        status: productForm.lifecycle.status,
        publish_status: productForm.lifecycle.publishStatus,
        browse_visibility: productForm.lifecycle.browseVisibility,
        product_type_key: productForm.classification.productTypeId || '',
        service_class: productForm.classification.serviceClass || '',
        service_group: productForm.classification.serviceGroup || '',
        default_variant_id: productForm.defaultVariantId || '',
        sort_order: productForm.lifecycle.sortOrder,
        metadata: metadataEntriesToRecord(productForm.metadata),
      });
      await refreshProduct();
      setProductEditorOpen(false);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to save product.'));
      }
    } finally {
      setSavingKind(null);
    }
  };

  const saveVariant = async () => {
    if (!product) {
      return;
    }

    const replaceAttributes = variantEditorMode === 'create' || variantAttributesDirty;
    const nextValidation = { ...validateVariantForm(variantForm, attributeDefinitionViewModels, replaceAttributes), ...validateVariantSpecifications(variantForm.specifications) };
    setVariantValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('variant');
    setError(null);

    try {
      const attributes = replaceAttributes
        ? buildVariantAttributePayload(variantForm.attributes, attributeDefinitionViewModels)
        : undefined;
      const payload = {
        ...(variantEditorMode === 'create' || variantSpecificationsDirty ? { specifications: buildVariantSpecificationsPayload(variantForm.specifications) } : {}),
        name: variantForm.identity.name.trim(),
        display_name: variantForm.identity.displayName || '',
        status: variantForm.lifecycle.status,
        sellable: variantForm.lifecycle.sellable,
        installation_required: variantForm.fulfillment.installationPolicy === 'required',
        fulfillment_code: variantForm.fulfillment.fulfillmentCode || '',
        inventory_policy: variantForm.fulfillment.inventoryPolicy || '',
        sort_order: variantForm.lifecycle.sortOrder,
        option_value_ids: variantForm.optionValueIds,
        metadata: metadataEntriesToRecord(variantForm.metadata),
      };
      await (
        variantEditorMode === 'edit' && editingVariantId
          ? updateVariant(editingVariantId, replaceAttributes ? { ...payload, attributes } : payload)
          : createVariant(product.product_id, { ...payload, attributes })
      );
      await refreshProduct();
      setVariantEditorOpen(false);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const message = getCommerceErrorMessage(err, 'Failed to save variant.');
        const attributeErrors = mapVariantAttributeServiceError(message, variantForm.attributes);
        if (attributeErrors) {
          setVariantValidation(current => ({ ...current, ...attributeErrors }));
        } else {
          setError(message);
        }
      }
    } finally {
      setSavingKind(null);
    }
  };

  const saveOffer = async () => {
    if (!selectedVariant) {
      return;
    }

    const nextValidation = validateOfferForm(offerForm);
    setOfferValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('offer');
    setError(null);

    try {
      const payload = {
        name: offerForm.identity.name.trim(),
        display_name: offerForm.identity.displayName || '',
        offer_type: offerForm.commercial.offerType,
        billing_period_months: offerForm.commercial.billingPeriodMonths ?? null,
        status: offerForm.lifecycle.status,
        sellable: offerForm.lifecycle.sellable,
        is_default: offerForm.lifecycle.isDefault,
        term_months:
          offerForm.commercial.offerType === equipmentLeaseRecurringOfferType
            ? null
            : offerForm.commercial.termMonths ?? null,
        fulfillment_code: offerForm.commercial.fulfillmentCode || '',
        sort_order: offerForm.lifecycle.sortOrder,
        metadata: metadataEntriesToRecord(offerForm.metadata),
      };

      await (
        offerEditorMode === 'edit' && editingOfferId
          ? updateVariantOffer(editingOfferId, payload)
          : createVariantOffer(selectedVariant.variant_id, payload)
      );
      await refreshProduct();
      setOfferEditorOpen(false);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to save offer.'));
      }
    } finally {
      setSavingKind(null);
    }
  };

  const saveRelatedItem = async () => {
    if (!selectedVariant) {
      return;
    }

    const bundleCurrency = catalog?.default_currency || 'USD';
    const nextValidation = validateRelatedItemForm(relatedItemForm, bundleCurrency);
    setRelatedItemValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('related-item');
    setError(null);

    try {
      const isEdit = relatedItemEditorMode === 'edit' && editingRelatedItemId;
      if (relatedItemForm.kind === 'included') {
        const payload = {
          component_product_id: relatedItemForm.productId || undefined,
          component_variant_id: relatedItemForm.variantId || undefined,
          required: relatedItemForm.required,
          default_quantity: Number.parseInt(relatedItemForm.defaultQuantity.trim(), 10),
          bundle_price_mode: relatedItemForm.bundlePriceMode,
          ...(relatedItemForm.bundlePriceMode === 'fixed_price' || relatedItemForm.bundlePriceMode === 'amount_off'
            ? {
                bundle_price_amount_minor: parseCurrencyInputToMinor(
                  relatedItemForm.bundlePriceAmount,
                  bundleCurrency,
                  'Bundle price',
                ),
              }
            : {}),
          ...(relatedItemForm.bundlePriceMode === 'percent_off'
            ? { bundle_price_percentage: Number(relatedItemForm.bundlePricePercentage) }
            : {}),
        };
        await (
          isEdit
            ? updateVariantBundleComponent(selectedVariant.variant_id, editingRelatedItemId, payload)
            : createVariantBundleComponent(selectedVariant.variant_id, payload)
        );
      } else {
        const payload = {
          recommended_product_id: relatedItemForm.productId || undefined,
          recommended_variant_id: relatedItemForm.variantId || undefined,
          reason: relatedItemForm.reason.trim() || undefined,
          sort_order: Number.parseInt(relatedItemForm.sortOrder.trim(), 10),
        };
        await (
          isEdit
            ? updateVariantRecommendation(selectedVariant.variant_id, editingRelatedItemId, payload)
            : createVariantRecommendation(selectedVariant.variant_id, payload)
        );
      }
      await refreshProduct();
      setRelatedItemEditorOpen(false);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to save related item.'));
      }
    } finally {
      setSavingKind(null);
    }
  };

  const saveFeature = async () => {
    if (!selectedVariant) {
      return;
    }

    const nextValidation = validateFeatureForm(featureForm);
    setFeatureValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    setSavingKind('feature');
    setError(null);

    try {
      const payload = {
        description: featureForm.description.trim(),
        icon: featureForm.icon,
        sort_order: Number.parseInt(featureForm.sortOrder.trim(), 10),
      };

      await (
        featureEditorMode === 'edit' && editingFeatureId
          ? updateVariantFeature(selectedVariant.variant_id, editingFeatureId, payload)
          : createVariantFeature(selectedVariant.variant_id, payload)
      );
      await refreshProduct();
      setFeatureEditorOpen(false);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to save feature.'));
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteProductRecord = async () => {
    if (!product) {
      return;
    }

    setSavingKind('product');
    setError(null);
    try {
      await archiveProduct(product.product_id);
      toast.success('Product deleted', {
        description: product.name,
      });
      setProductEditorOpen(false);
      navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(catalogId), navigation.path));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete product.');
        setError(msg);
        toast.error('Failed to delete product', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteVariant = async (variant: ProductVariant) => {
    setSavingKind('variant');
    setError(null);
    try {
      await archiveVariant(variant.variant_id);
      await refreshProduct();
      toast.success('Variant deleted', {
        description: variant.display_name || variant.name,
      });
      if (editingVariantId === variant.variant_id) {
        setVariantEditorOpen(false);
        setEditingVariantId(null);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete variant.');
        setError(msg);
        toast.error('Failed to delete variant', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteOffer = async (offer: ProductVariantOffer) => {
    setSavingKind('offer');
    setError(null);
    try {
      await archiveVariantOffer(offer.offer_id);
      await refreshProduct();
      toast.success('Offer deleted', {
        description: offer.display_name || offer.name,
      });
      if (editingOfferId === offer.offer_id) {
        setOfferEditorOpen(false);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete offer.');
        setError(msg);
        toast.error('Failed to delete offer', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteRecommendation = async (recommendation: VariantRecommendation) => {
    if (!selectedVariant) {
      return;
    }
    const targetLabel =
      variantDetailsById.get(recommendation.recommended_variant_id || '')?.label ||
      productLabelById.get(recommendation.recommended_product_id || '') ||
      'this recommendation';

    setSavingKind('related-item');
    setError(null);
    try {
      await archiveVariantRecommendation(selectedVariant.variant_id, recommendation.recommendation_id);
      await refreshProduct();
      toast.success('Suggested item removed', {
        description: targetLabel,
      });
      if (editingRelatedItemId === recommendation.recommendation_id) {
        setRelatedItemEditorOpen(false);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete recommendation.');
        setError(msg);
        toast.error('Failed to delete recommendation', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteBundleComponent = async (component: VariantBundleComponent) => {
    if (!selectedVariant) {
      return;
    }
    const targetLabel =
      variantDetailsById.get(component.component_variant_id || '')?.label ||
      productLabelById.get(component.component_product_id || '') ||
      'this bundle component';

    setSavingKind('related-item');
    setError(null);
    try {
      await archiveVariantBundleComponent(selectedVariant.variant_id, component.bundle_component_id);
      await refreshProduct();
      toast.success('Included item removed', {
        description: targetLabel,
      });
      if (editingRelatedItemId === component.bundle_component_id) {
        setRelatedItemEditorOpen(false);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete bundle component.');
        setError(msg);
        toast.error('Failed to delete bundle component', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const deleteFeature = async (feature: VariantFeature) => {
    if (!selectedVariant) {
      return;
    }
    const targetLabel = feature.description || 'this feature';

    setSavingKind('feature');
    setError(null);
    try {
      await archiveVariantFeature(selectedVariant.variant_id, feature.feature_id);
      await refreshProduct();
      toast.success('Feature deleted', {
        description: targetLabel,
      });
      if (editingFeatureId === feature.feature_id) {
        setFeatureEditorOpen(false);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete feature.');
        setError(msg);
        toast.error('Failed to delete feature', { description: msg });
      }
    } finally {
      setSavingKind(null);
    }
  };

  const confirmDelete = (title: string, description: string, onConfirm: () => void) => {
    setConfirmAction({ title, description, onConfirm });
  };

  const mediaManagerViewModel = useCatalogProductMediaController({
    product,
    loading,
    loadError: product ? null : error,
    refreshProduct,
    onAccessDenied: setAccessDeniedStatus,
    onError: message => {
      setError(message);
      toast.error('Product image action failed', { description: message });
    },
    onSuccess: (title, description) => toast.success(title, { description }),
    confirmDelete,
  });

  const bundleComponentRows =
    selectedVariant?.bundle_components?.length
      ? selectedVariant.bundle_components.map(component => {
          const productLabel = component.component_product_id
            ? productLabelById.get(component.component_product_id) || component.component_product_id
            : null;
          const variantLabel = component.component_variant_id
            ? variantDetailsById.get(component.component_variant_id)?.label || component.component_variant_id
            : null;
          return (
            <div key={component.bundle_component_id} className="group px-5 py-4 transition-all hover:bg-muted/30">
              <HorizontalStack className="items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <VerticalStack gap={2}>
                    <HorizontalStack className="items-center gap-2">
                      <p className="text-sm font-black text-foreground">
                        {variantLabel || productLabel || 'Included item target'}
                      </p>
                      <RegistryStatus
                        label={component.required ? 'Included · Required' : 'Included · Optional'}
                        variant={component.required ? 'warning' : 'default'}
                      />
                      <RegistryStatus label={`Qty ${component.default_quantity || 1}`} variant="default" />
                    </HorizontalStack>
                    {productLabel ? (
                      <DescriptionText className="line-clamp-1 text-[11px]">
                        Product: {productLabel}
                      </DescriptionText>
                    ) : null}
                    {variantLabel ? (
                      <DescriptionText className="line-clamp-1 text-[11px]">
                        Variant: {variantLabel}
                      </DescriptionText>
                    ) : null}
                    <DescriptionText className="line-clamp-2 text-[11px]">
                      {component.required
                        ? 'Auto-attaches with the parent variant during checkout.'
                        : 'Available as a non-required bundle child for the parent variant.'}
                    </DescriptionText>
                  </VerticalStack>
                </div>

                <HorizontalStack gap={2}>
                  <button
                    type="button"
                    onClick={() => openBundleComponentEdit(component)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      confirmDelete(
                        'Delete bundle component?',
                        `${variantLabel || productLabel || 'This included item'} will no longer be attached to this variant.`,
                        () => void deleteBundleComponent(component),
                      )
                    }
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                  >
                    Delete
                  </button>
                </HorizontalStack>
              </HorizontalStack>
            </div>
          );
        })
      : null;

  const featureRows =
    selectedVariant?.features?.length
      ? selectedVariant.features.map(feature => (
          <div key={feature.feature_id} className="group px-5 py-4 transition-all hover:bg-muted/30">
            <HorizontalStack className="items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <VerticalStack gap={2}>
                  <HorizontalStack className="items-center gap-2">
                    <FeatureIcon name={feature.icon} />
                    <p className="text-sm font-black text-foreground">{feature.description}</p>
                  </HorizontalStack>
                  <DescriptionText className="line-clamp-1 text-[11px]">
                    {feature.icon ? `Icon: ${feature.icon}` : 'No icon configured.'}
                  </DescriptionText>
                </VerticalStack>
              </div>

              <HorizontalStack gap={2}>
                <button
                  type="button"
                  onClick={() => openFeatureEdit(feature)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() =>
                    confirmDelete(
                      'Delete feature?',
                      `"${feature.description}" will be removed from this variant.`,
                      () => void deleteFeature(feature),
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                >
                  Delete
                </button>
              </HorizontalStack>
            </HorizontalStack>
          </div>
        ))
      : null;

  const recommendationRows =
    selectedVariant?.recommendations?.length
      ? selectedVariant.recommendations.map(recommendation => {
          const productLabel = recommendation.recommended_product_id
            ? productLabelById.get(recommendation.recommended_product_id) || recommendation.recommended_product_id
            : null;
          const variantLabel = recommendation.recommended_variant_id
            ? variantDetailsById.get(recommendation.recommended_variant_id)?.label || recommendation.recommended_variant_id
            : null;
          return (
            <div key={recommendation.recommendation_id} className="group px-5 py-4 transition-all hover:bg-muted/30">
              <HorizontalStack className="items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <VerticalStack gap={2}>
                    <HorizontalStack className="items-center gap-2">
                      <p className="text-sm font-black text-foreground">
                        {variantLabel || productLabel || 'Recommendation target'}
                      </p>
                      <RegistryStatus label="Suggested" variant="default" />
                    </HorizontalStack>

                    {productLabel ? (
                      <DescriptionText className="line-clamp-1 text-[11px]">
                        Product: {productLabel}
                      </DescriptionText>
                    ) : null}
                    {variantLabel ? (
                      <DescriptionText className="line-clamp-1 text-[11px]">
                        Variant: {variantLabel}
                      </DescriptionText>
                    ) : null}
                    <DescriptionText className="line-clamp-2 text-[11px]">
                      {recommendation.reason || 'No operator guidance provided.'}
                    </DescriptionText>
                  </VerticalStack>
                </div>

                <HorizontalStack gap={2}>
                  <button
                    type="button"
                    onClick={() => openRecommendationEdit(recommendation)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      confirmDelete(
                        'Delete recommendation?',
                        `${variantLabel || productLabel || 'This recommendation'} will no longer be suggested with this variant.`,
                        () => void deleteRecommendation(recommendation),
                      )
                    }
                    className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                  >
                    Delete
                  </button>
                </HorizontalStack>
              </HorizontalStack>
            </div>
          );
        })
      : null;

  const variantCount = product?.variants.length || 0;
  const sellableVariantCount = (product?.variants || []).filter(variant => variant.sellable).length;
  const variantsMissingOffers = (product?.variants || []).filter(
    variant => (variant.offers?.length || 0) === 0,
  ).length;

  // Offers/variants that carry a price row in any non-archived pricebook.
  // null means the pricebook lookup was unavailable, so no coverage claims are made.
  const pricedTargets = useMemo(() => {
    if (!pricebooks) {
      return null;
    }
    const offerIds = new Set<string>();
    const variantIds = new Set<string>();
    pricebooks.forEach(pricebook => {
      if (pricebook.status === 'archived') {
        return;
      }
      pricebook.offer_prices?.forEach(row => {
        if (row.offer_id) offerIds.add(row.offer_id);
        if (row.variant_id) variantIds.add(row.variant_id);
      });
      pricebook.variant_prices?.forEach(row => {
        if (row.variant_id) variantIds.add(row.variant_id);
      });
    });
    return { offerIds, variantIds };
  }, [pricebooks]);

  const variantsWithOffers = variantCount - variantsMissingOffers;

  const workspaceModeSwitchViewModel: CatalogProductWorkspaceModeSwitchViewModel = {
    activeMode: workspaceMode,
    variantCount,
    imageCount: product?.media?.length || 0,
    onModeChange: selectWorkspaceMode,
  };
  const variantsMissingPricing = useMemo(() => {
    if (!pricedTargets) {
      return 0;
    }
    return (product?.variants || []).filter(
      variant =>
        (variant.offers?.length || 0) > 0 &&
        !pricedTargets.variantIds.has(variant.variant_id) &&
        !(variant.offers || []).some(offer => pricedTargets.offerIds.has(offer.offer_id)),
    ).length;
  }, [pricedTargets, product]);

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    searchQuery,
    searchPlaceholder: 'Filter variants...',
    onSearchChange: query.setSearch,
    error,
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
  });

  const productEditorViewModel: ProductEditorViewModel = {
    mode: 'edit',
    state: savingKind === 'product' ? 'saving' : 'ready',
    isOpen: productEditorOpen,
    title: 'Edit Product',
    subtitle: 'Update the product metadata without leaving the variant workspace.',
    saveActionLabel: 'Save Product',
    productId: product?.product_id || null,
    value: productForm,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    publishStatusOptions: [
      { value: 'unpublished', label: 'Unpublished' },
      { value: 'published', label: 'Published' },
    ],
    browseVisibilityOptions: [...productBrowseVisibilityOptions],
    productTypeOptions: [
      { value: '', label: 'Unset' },
      { value: 'one_time', label: 'One-Time' },
      { value: 'subscription', label: 'Subscription' },
      { value: 'bundle', label: 'Bundle' },
    ],
    serviceClassOptions: [
      { value: '', label: 'Unset' },
      { value: 'internet', label: 'Internet' },
      { value: 'voice', label: 'Voice' },
      { value: 'video', label: 'Video' },
      { value: 'managed_service', label: 'Managed Service' },
    ],
    serviceGroupOptions: [
      { value: '', label: 'Unset' },
      { value: 'residential', label: 'Residential' },
      { value: 'business', label: 'Business' },
      { value: 'enterprise', label: 'Enterprise' },
    ],
    defaultVariantOptions: [
      { value: '', label: 'No default variant' },
      ...((product?.variants || []).map(variant => ({
        value: variant.variant_id,
        label: variant.display_name || variant.name,
      })) || []),
    ],
    fieldHelp: [],
    validationErrors: productValidation,
    onChange: patch => setProductForm(current => ({ ...current, ...patch })),
    onSave: saveProduct,
    onClose: () => setProductEditorOpen(false),
  };

  const variantEditorViewModel: VariantEditorViewModel = {
    mode: variantEditorMode,
    state: savingKind === 'variant' ? 'saving' : 'ready',
    isOpen: variantEditorOpen,
    title: variantEditorMode === 'edit' ? 'Edit Variant' : 'Create Variant',
    subtitle:
      variantEditorMode === 'edit'
        ? 'Refine the sellable unit details for this product.'
        : 'Add a new sellable unit to this product.',
    saveActionLabel: variantEditorMode === 'edit' ? 'Save Variant' : 'Create Variant',
    variantId: editingVariantId,
    value: variantForm,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    installationPolicyOptions: [
      { value: 'not_required', label: 'Not Required' },
      { value: 'required', label: 'Required' },
    ],
    inventoryPolicyOptions: [
      { value: '', label: 'Unset' },
      { value: 'tracked', label: 'Tracked' },
      { value: 'untracked', label: 'Untracked' },
      { value: 'reservation', label: 'Reservation' },
    ],
    optionValueOptions: flattenOptionValueOptions(product),
    attributeDefinitions: attributeDefinitionViewModels,
    fieldHelp: [],
    validationErrors: variantValidation,
    onAddSpecification: () => {
      setVariantSpecificationsDirty(true);
      setVariantForm(current => ({ ...current, specifications: [...current.specifications, { id: crypto.randomUUID(), group: '', name: '', value: '' }] }));
    },
    onUpdateSpecification: (id, patch) => {
      setVariantSpecificationsDirty(true);
      setVariantForm(current => ({ ...current, specifications: current.specifications.map(row => row.id === id ? { ...row, ...patch } : row) }));
    },
    onRemoveSpecification: id => {
      setVariantSpecificationsDirty(true);
      setVariantForm(current => ({ ...current, specifications: current.specifications.filter(row => row.id !== id) }));
    },
    onMoveSpecification: (id, direction) => {
      setVariantSpecificationsDirty(true);
      setVariantForm(current => {
        const rows = [...current.specifications];
        const index = rows.findIndex(row => row.id === id);
        const next = index + direction;
        if (index < 0 || next < 0 || next >= rows.length) return current;
        [rows[index], rows[next]] = [rows[next], rows[index]];
        return { ...current, specifications: rows };
      });
    },
    onChange: patch => setVariantForm(current => ({ ...current, ...patch })),
    onAddAttribute: (attributeKey) => {
      const usedKeys = new Set(variantForm.attributes.map(attribute => attribute.attributeKey));
      const definition = attributeDefinitionViewModels.find(item => !usedKeys.has(item.attributeKey) && (!attributeKey || item.attributeKey === attributeKey));
      setVariantForm(current => ({
        ...current,
        attributes: [
          ...current.attributes,
          {
            id: `attribute-${attributeRowSequence.current++}`,
            attributeKey: definition?.attributeKey || '',
            value: initialVariantAttributeValue(definition?.attributeKey, definition?.valueType),
            unit: 'Mbps',
          },
        ],
      }));
      setVariantAttributesDirty(true);
    },
    onUpdateAttribute: (id, patch) => {
      setVariantForm(current => ({
        ...current,
        attributes: current.attributes.map(attribute => {
          if (attribute.id !== id) {
            return attribute;
          }
          if (patch.attributeKey !== undefined && patch.attributeKey !== attribute.attributeKey) {
            const definition = attributeDefinitionViewModels.find(item => item.attributeKey === patch.attributeKey);
            return {
              ...attribute,
              ...patch,
              value: initialVariantAttributeValue(definition?.attributeKey, definition?.valueType),
              unit: 'Mbps',
            };
          }
          return { ...attribute, ...patch };
        }),
      }));
      setVariantAttributesDirty(true);
    },
    onRemoveAttribute: id => {
      setVariantForm(current => ({
        ...current,
        attributes: current.attributes.filter(attribute => attribute.id !== id),
      }));
      setVariantAttributesDirty(true);
    },
    onSave: saveVariant,
    onClose: () => setVariantEditorOpen(false),
  };

  const offerEditorViewModel: VariantOfferEditorViewModel = {
    mode: offerEditorMode,
    state: savingKind === 'offer' ? 'saving' : 'ready',
    isOpen: offerEditorOpen,
    title: offerEditorMode === 'edit' ? 'Edit Offer' : 'Create Offer',
    subtitle: selectedVariant
      ? `Manage the commercial terms for ${selectedVariant.display_name || selectedVariant.name}.`
      : 'Manage the commercial terms for this variant.',
    saveActionLabel: offerEditorMode === 'edit' ? 'Save Offer' : 'Create Offer',
    offerId: editingOfferId,
    value: offerForm,
    offerTypeOptions: [
      { value: 'subscription', label: 'Subscription' },
      { value: 'purchase', label: 'One-time purchase' },
      { value: 'rent', label: 'Rental' },
      { value: 'installment', label: 'Installment' },
      { value: equipmentLeaseRecurringOfferType, label: 'Equipment Lease Recurring' },
      { value: 'customer_owned', label: 'Customer Owned' },
      { value: 'trial', label: 'Trial' },
    ],
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    metrics: selectedVariant
      ? [
          {
            id: 'offer-variant',
            label: 'Variant',
            value: selectedVariant.display_name || selectedVariant.name,
          },
          {
            id: 'offer-count',
            label: 'Existing Offers',
            value: String(selectedVariant.offers?.length || 0),
          },
          {
            id: 'offer-default',
            label: 'Default Offer',
            value:
              selectedVariant.offers?.find(offer => offer.is_default)?.display_name ||
              selectedVariant.offers?.find(offer => offer.is_default)?.name ||
              'Not set',
          },
        ]
      : [],
    fieldHelp: [],
    validationErrors: offerValidation,
    onChange: patch => setOfferForm(current => ({ ...current, ...patch })),
    onSave: saveOffer,
    onClose: () => setOfferEditorOpen(false),
  };

  const offerRows =
    selectedVariant?.offers?.length
      ? selectedVariant.offers.map(offer => (
          <div key={offer.offer_id} className="group px-5 py-4 transition-all hover:bg-muted/30">
            <HorizontalStack className="items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <VerticalStack gap={2}>
                  <HorizontalStack className="items-center gap-2">
                    <p className="text-sm font-black text-foreground">
                      {offer.display_name || offer.name}
                    </p>
                    <RegistryStatus label={offer.status} variant={statusVariant(offer.status)} />
                    {offer.is_default ? (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-primary">
                        Default
                      </span>
                    ) : null}
                  </HorizontalStack>

                  <DescriptionText className="line-clamp-1 text-[11px]">
                    {offer.offer_key}
                  </DescriptionText>

                  {buildOfferCommercialSummary(offer) ? (
                    <DescriptionText className="line-clamp-2 text-[11px]">
                      {buildOfferCommercialSummary(offer)}
                    </DescriptionText>
                  ) : null}

                  <DescriptionText className="text-[11px]">
                    {[
                      offer.offer_type,
                      offer.offer_type === equipmentLeaseRecurringOfferType
                        ? null
                        : offer.term_months
                          ? `${offer.term_months} month term`
                          : 'no term',
                      offer.sellable ? null : 'internal only',
                      pricedTargets &&
                      !pricedTargets.offerIds.has(offer.offer_id) &&
                      !pricedTargets.variantIds.has(selectedVariant?.variant_id || '')
                        ? 'no price set'
                        : null,
                      `updated ${formatRelativeTime(offer.updated_at)}`,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </DescriptionText>
                </VerticalStack>
              </div>

              <HorizontalStack gap={2}>
                <button
                  type="button"
                  onClick={() => openOfferEdit(offer)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() =>
                    confirmDelete(
                      'Delete offer?',
                      `"${offer.display_name || offer.name}" will no longer be available for this variant.`,
                      () => void deleteOffer(offer),
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                >
                  Delete
                </button>
              </HorizontalStack>
            </HorizontalStack>
          </div>
        ))
      : null;

  return (
    <>
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[
              { label: 'Catalogs', onSelect: () => navigation.navigate(withCommerceRegistryQuery(buildCatalogDirectoryRoute(), navigation.path)) },
              { label: catalog?.name || 'Catalog', onSelect: () => navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(catalogId), navigation.path)) },
              { label: product?.name || 'Product' },
            ]}
            title={product?.name || 'Product'}
            description="Manage the sellable variants and fulfillment metadata for this product."
            onBack={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(catalogId), navigation.path))}
          />
        }
      >
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center py-24 text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="mt-4 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
              Loading Variant Inventory
            </p>
          </div>
        ) : product ? (
          <div className="flex h-full min-h-0 flex-col">
            <CatalogProductWorkspaceModeSwitch
              viewModel={workspaceModeSwitchViewModel}
              className="flex-shrink-0 px-4 sm:px-5"
            />
            {workspaceMode === 'images' ? (
              <div
                id="images-panel"
                role="tabpanel"
                aria-labelledby="tab-images"
                className="min-h-0 flex-1 overflow-y-auto"
              >
                <CatalogProductMediaManager viewModel={mediaManagerViewModel} />
              </div>
            ) : (
            <div
              id="variants-panel"
              role="tabpanel"
              aria-labelledby="tab-variants"
              className="contents"
            >
            <CommerceRegistrySearchField
              value={searchQuery}
              onChange={query.setSearch}
              placeholder="Search product variants..."
              resultCount={filteredVariants.length}
            />
            {/* ── Setup checklist: the path from draft to sellable ── */}
            <div className="flex flex-shrink-0 flex-wrap items-center gap-x-1 gap-y-1 border-b border-border/40 bg-muted/5 px-4 py-2 sm:px-5">
              <SetupStep
                done={product.publish_status === 'published'}
                label={product.publish_status === 'published' ? 'Published' : 'Publish product'}
                onClick={product.publish_status === 'published' ? undefined : openProductEdit}
              />
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/30" />
              <SetupStep
                done={variantCount > 0}
                label={
                  variantCount > 0
                    ? `Variants (${variantCount} · ${sellableVariantCount} sellable)`
                    : 'Add a variant'
                }
                onClick={variantCount > 0 ? undefined : openVariantCreate}
              />
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/30" />
              <SetupStep
                done={variantCount > 0 && variantsMissingOffers === 0}
                warn={variantsMissingOffers > 0}
                label={
                  variantCount === 0
                    ? 'Add offers'
                    : variantsMissingOffers > 0
                      ? `Offers (${variantsMissingOffers} ${variantsMissingOffers === 1 ? 'variant' : 'variants'} missing)`
                      : 'Offers'
                }
                onClick={() => setDetailTab('offers')}
              />
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/30" />
              <SetupStep
                done={pricedTargets !== null && variantsWithOffers > 0 && variantsMissingPricing === 0}
                warn={pricedTargets !== null && variantsMissingPricing > 0}
                label={
                  pricedTargets === null || variantsWithOffers === 0
                    ? 'Set prices in Pricebooks'
                    : variantsMissingPricing > 0
                      ? `Prices (${variantsMissingPricing} ${variantsMissingPricing === 1 ? 'variant' : 'variants'} unpriced)`
                      : 'Prices set'
                }
                onClick={() => navigation.navigate(buildPricebooksListRoute())}
              />
            </div>

            {/* ── Split-panel layout ─────────────────────────────────────── */}
            <div className="flex min-h-0 flex-1">

            {/* ── LEFT PANE: product header + variant navigator + media ─── */}
            <div
              className={
                'flex flex-col border-r border-border/40 overflow-hidden ' +
                'w-full lg:w-80 xl:w-[360px] lg:flex-shrink-0 ' +
                (mobileShowDetail ? 'hidden lg:flex' : 'flex')
              }
            >
              {/* Product header card */}
              <div className="flex-shrink-0 border-b border-border/40 bg-muted/5 px-4 py-4 sm:px-5 sm:py-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-black text-foreground">{product.name}</h2>
                      <RegistryStatus label={product.status} variant={statusVariant(product.status)} />
                    </div>
                    <p className="line-clamp-2 text-[11px] leading-relaxed text-muted-foreground/60">
                      {product.short_description || product.description || product.product_key}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      onClick={openProductEdit}
                      title="Edit product"
                      className="inline-flex items-center justify-center rounded-md border border-border/60 bg-background p-1.5 text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        confirmDelete(
                          'Delete product?',
                          `"${product.name}" and its variants will be archived.`,
                          () => void deleteProductRecord(),
                        )
                      }
                      title="Delete product"
                      className="inline-flex items-center justify-center rounded-md border border-border/60 bg-background p-1.5 text-muted-foreground hover:border-destructive/40 hover:text-destructive transition-all"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Variants section header */}
              <div className="flex-shrink-0 flex items-center justify-between border-b border-border/30 px-4 py-2.5 sm:px-5">
                <span className="text-xs font-semibold text-muted-foreground">
                  Variants
                  {filteredVariants.length > 0 && (
                    <span className="ml-1.5 rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[10px]">
                      {filteredVariants.length}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={openVariantCreate}
                  className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <Plus className="h-3 w-3" />
                  New Variant
                </button>
              </div>

              {/* Variant list — scrolls independently */}
              <div className="min-h-0 flex-1 overflow-y-auto divide-y divide-border/20">
                {filteredVariants.length > 0 ? (
                  filteredVariants.map(variant => {
                    const offerCount = variant.offers?.length || 0;
                    const isSelected = selectedVariant?.variant_id === variant.variant_id;
                    return (
                      <button
                        key={variant.variant_id}
                        type="button"
                        onClick={() => {
                          selectVariant(variant.variant_id);
                          setMobileShowDetail(true);
                        }}
                        className={
                          'w-full border-l-2 px-4 py-3.5 text-left transition-all hover:bg-muted/30 sm:px-5 ' +
                          (isSelected
                            ? 'border-l-primary bg-primary/5'
                            : 'border-l-transparent hover:border-l-border/50')
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span
                                className={
                                  'text-sm font-black ' +
                                  (isSelected ? 'text-primary' : 'text-foreground')
                                }
                              >
                                {variant.display_name || variant.name}
                              </span>
                              <RegistryStatus label={variant.status} variant={statusVariant(variant.status)} />
                              {product.default_variant_id === variant.variant_id && (
                                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-primary">
                                  Default
                                </span>
                              )}
                            </div>
                          </div>
                          <span
                            className={
                              'shrink-0 rounded px-1.5 py-0.5 text-[9px] font-black ' +
                              (offerCount === 0
                                ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                                : 'border border-border/40 bg-muted/60 text-muted-foreground/70')
                            }
                          >
                            {offerCount} {offerCount === 1 ? 'offer' : 'offers'}
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-12 text-center">
                    <p className="text-sm font-black text-foreground">No variants</p>
                    <DescriptionText className="mt-1 text-[11px]">
                      {normalizedSearch
                        ? 'No variants match the search.'
                        : 'A product groups the variants a customer can choose from, and each variant needs at least one offer before it can be sold. Create a variant to get started.'}
                    </DescriptionText>
                    {!normalizedSearch && (
                      <button
                        type="button"
                        onClick={openVariantCreate}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-all"
                      >
                        <Plus className="h-3 w-3" />
                        New Variant
                      </button>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* ── RIGHT PANE: variant detail editor ───────────────────── */}
            <div
              className={
                'min-w-0 flex-1 overflow-y-auto ' +
                (!mobileShowDetail ? 'hidden lg:block' : 'block')
              }
            >
              {selectedVariant ? (
                <>
                  {/* Mobile back navigation */}
                  <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border/40 bg-background/95 px-4 py-3 backdrop-blur-sm lg:hidden">
                    <button
                      type="button"
                      onClick={() => setMobileShowDetail(false)}
                      className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      All Variants
                    </button>
                    <span className="text-xs text-muted-foreground/40">/</span>
                    <span className="truncate text-xs font-black text-foreground">
                      {selectedVariant.display_name || selectedVariant.name}
                    </span>
                  </div>

                  {/* Variant identity header */}
                  <div className="border-b border-border/40 bg-muted/5 px-5 py-5 sm:px-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-black text-foreground">
                            {selectedVariant.display_name || selectedVariant.name}
                          </h3>
                          <RegistryStatus label={selectedVariant.status} variant={statusVariant(selectedVariant.status)} />
                          <RegistryStatus
                            label={selectedVariant.sellable ? 'Sellable' : 'Internal'}
                            variant={sellableVariant(selectedVariant.sellable)}
                          />
                          {product.default_variant_id === selectedVariant.variant_id && (
                            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-primary">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1">
                          <span className="text-[11px] text-muted-foreground/70">
                            <span className="mr-1.5 text-[9px] font-black uppercase tracking-wide text-muted-foreground/40">Key</span>
                            {selectedVariant.variant_key}
                          </span>
                          <span className="text-[11px] text-muted-foreground/70">
                            <span className="mr-1.5 text-[9px] font-black uppercase tracking-wide text-muted-foreground/40">Inventory</span>
                            {selectedVariant.inventory_policy || 'untracked'}
                          </span>
                          {selectedVariant.fulfillment_code && (
                            <span className="text-[11px] text-muted-foreground/70">
                              <span className="mr-1.5 text-[9px] font-black uppercase tracking-wide text-muted-foreground/40">Fulfillment</span>
                              {selectedVariant.fulfillment_code}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openVariantEdit(selectedVariant)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            confirmDelete(
                              'Delete variant?',
                              `"${selectedVariant.display_name || selectedVariant.name}" and its offers will be archived.`,
                              () => void deleteVariant(selectedVariant),
                            )
                          }
                          title="Delete variant"
                          className="inline-flex items-center justify-center rounded-md border border-border/60 bg-background p-1.5 text-muted-foreground hover:border-destructive/40 hover:text-destructive transition-all"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Detail tabs: Offers is the critical path; merchandising is optional polish */}
                  <div
                    role="tablist"
                    aria-label="Variant details"
                    className="flex gap-1 border-b border-border/40 px-5 sm:px-6"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={detailTab === 'offers'}
                      onClick={() => setDetailTab('offers')}
                      className={
                        '-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ' +
                        (detailTab === 'offers'
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground')
                      }
                    >
                      Offers
                      <span
                        className={
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold ' +
                          ((selectedVariant.offers?.length || 0) === 0
                            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                            : 'border border-border/40 bg-muted/60 text-muted-foreground/80')
                        }
                      >
                        {selectedVariant.offers?.length || 0}
                      </span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={detailTab === 'merchandising'}
                      onClick={() => setDetailTab('merchandising')}
                      className={
                        '-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ' +
                        (detailTab === 'merchandising'
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground')
                      }
                    >
                      Merchandising
                      <span className="rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground/80">
                        {(selectedVariant.bundle_components?.length || 0) +
                          (selectedVariant.features?.length || 0) +
                          (selectedVariant.recommendations?.length || 0)}
                      </span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={detailTab === 'specifications'}
                      onClick={() => setDetailTab('specifications')}
                      className={'-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ' + (detailTab === 'specifications' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                    >
                      Specifications
                      <span className="rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground/80">{selectedVariant.specifications?.length || 0}</span>
                    </button>
                  </div>

                  {detailTab === 'offers' ? (
                  <section aria-label="Offers">
                    <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Offers define how customers acquire this variant — subscription, purchase, rental, installment, or trial.
                      </p>
                      <button
                        type="button"
                        onClick={openOfferCreate}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Offer
                      </button>
                    </div>

                    {/* No-offers warning */}
                    {!(selectedVariant.offers?.length) && (
                      <div className="mx-5 mb-5 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3.5 sm:mx-6">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="min-w-0">
                          <p className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                            No offers — this variant cannot be sold
                          </p>
                          <p className="text-[11px] leading-relaxed text-amber-700/80 dark:text-amber-300/80">
                            Pricebooks and storefronts require at least one active offer before this variant appears in selling flows. Use Add Offer to create the first one.
                          </p>
                        </div>
                      </div>
                    )}

                    {offerRows && (
                      <div className="divide-y divide-border/30 border-t border-border/30">
                        {offerRows}
                      </div>
                    )}
                  </section>
                  ) : null}

                  {detailTab === 'specifications' ? (
                  <VariantSpecificationsDisplay rows={selectedVariant.specifications || []} onEdit={() => openVariantEdit(selectedVariant)} />
                  ) : null}

                  {detailTab === 'merchandising' ? (
                  <>
                  <section aria-label="Related Items" className="border-b border-border/40">
                    <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Related Items
                          <span className="ml-2 rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground/80">
                            {(selectedVariant.bundle_components?.length || 0) +
                              (selectedVariant.recommendations?.length || 0)}
                          </span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Included items ship with this variant at checkout; suggested items are optional guidance for guided selling.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={openRelatedItemCreate}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </button>
                    </div>
                    {bundleComponentRows || recommendationRows ? (
                      <div className="divide-y divide-border/30 border-t border-border/30">
                        {bundleComponentRows}
                        {recommendationRows}
                      </div>
                    ) : (
                      <div className="border-t border-border/30 px-5 py-6 sm:px-6">
                        <DescriptionText className="text-[11px]">
                          No related items. Add one when this variant should include equipment at checkout or suggest add-ons during guided selling.
                        </DescriptionText>
                      </div>
                    )}
                  </section>

                  <section aria-label="Features">
                    <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Features
                          <span className="ml-2 rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground/80">
                            {selectedVariant.features?.length || 0}
                          </span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Selling points and customer benefits shown on storefront listings.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={openFeatureCreate}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </button>
                    </div>
                    {featureRows ? (
                      <div className="divide-y divide-border/30 border-t border-border/30">{featureRows}</div>
                    ) : (
                      <div className="border-t border-border/30 px-5 py-6 sm:px-6">
                        <DescriptionText className="text-[11px]">
                          No features. Add comparison bullets when this variant needs storefront feature highlights.
                        </DescriptionText>
                      </div>
                    )}
                  </section>

                  </>
                  ) : null}
                </>
              ) : (
                /* Desktop empty state when product has no variants yet */
                <div className="hidden h-full flex-col items-center justify-center px-6 py-24 text-center lg:flex">
                  <p className="text-sm font-black text-muted-foreground/60">No variant selected</p>
                  <DescriptionText className="mx-auto mt-1 max-w-xs text-[11px]">
                    A product groups the variants a customer can choose from, and each variant needs at least one offer before it can be sold. Create a variant to get started.
                  </DescriptionText>
                  <button
                    type="button"
                    onClick={openVariantCreate}
                    className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-all"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Variant
                  </button>
                </div>
              )}
            </div>
            </div>
            </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <span className="text-lg text-red-600">!</span>
            </div>
            <p className="text-sm font-black tracking-tight text-foreground">Product Unavailable</p>
            <DescriptionText className="mt-1.5 max-w-xs text-xs">
              {error || 'The requested product could not be loaded.'}
            </DescriptionText>
          </div>
        )}
      </CommerceCatalogWorkspace>

      <ProductEditorSheet editor={productEditorViewModel} />
      <VariantEditorSheet editor={variantEditorViewModel} />
      <VariantOfferEditorSheet editor={offerEditorViewModel} />
      <FeatureEditorSheet
        mode={featureEditorMode}
        isOpen={featureEditorOpen}
        saving={savingKind === 'feature'}
        variantLabel={selectedVariant?.display_name || selectedVariant?.name || 'this variant'}
        form={featureForm}
        validationErrors={featureValidation}
        onClose={() => setFeatureEditorOpen(false)}
        onChange={patch => setFeatureForm(current => ({ ...current, ...patch }))}
        onSave={saveFeature}
      />
      <RelatedItemEditorSheet
        mode={relatedItemEditorMode}
        isOpen={relatedItemEditorOpen}
        saving={savingKind === 'related-item'}
        variantLabel={selectedVariant?.display_name || selectedVariant?.name || 'this variant'}
        form={relatedItemForm}
        validationErrors={relatedItemValidation}
        productOptions={relatedItemProductOptions}
        variantOptions={relatedItemVariantOptions}
        onClose={() => setRelatedItemEditorOpen(false)}
        onKindChange={kind => setRelatedItemForm(current => ({ ...current, kind }))}
        onProductChange={value =>
          setRelatedItemForm(current => {
            const keepVariant =
              current.variantId && variantDetailsById.get(current.variantId)?.productId === value;
            return {
              ...current,
              productId: value,
              variantId: keepVariant ? current.variantId : '',
            };
          })
        }
        onVariantChange={value =>
          setRelatedItemForm(current => ({
            ...current,
            variantId: value,
            productId: value ? variantDetailsById.get(value)?.productId || current.productId : current.productId,
          }))
        }
        onChange={patch => setRelatedItemForm(current => ({ ...current, ...patch }))}
        onSave={saveRelatedItem}
      />
      {confirmAction ? (
        <>
          <div
            className="fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setConfirmAction(null)}
            aria-hidden="true"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label={confirmAction.title}
            className="fixed left-1/2 top-1/2 z-[70] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          >
            <p className="text-sm font-semibold text-foreground">{confirmAction.title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {confirmAction.description}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted/40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = confirmAction.onConfirm;
                  setConfirmAction(null);
                  action();
                }}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground transition-opacity hover:opacity-90"
              >
                Delete
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
